import React, { useEffect, useMemo, useState } from 'react';
import LeagueTab from './LeagueTab';
import MatchList from './MatchList';
import TabCategory from './TabCategory';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { selectGames, selectSearch, setSearch } from '../homeSlice';
import { GameResponseModel } from 'models';
import { GamesForTab, initialGamesForTab } from 'api/mockSportCategory';
import SearchLayout from '../layouts/SearchLayout';
import { isNotInLive } from 'utils/betUtils';
import { isLaptop, isPhone, isTablet } from 'utils/commonUtils';
import { platformStates } from 'redux/platformSlice';
import { compareCommenceTime } from 'utils/dateUtils';

interface BetBodyProps {
  isLoading?: boolean;
}

const checkContain = (homeTeam: string, awayTeam: string, searchText: string): boolean => {
  const searchTextLower = searchText.toLowerCase();
  return (
    homeTeam.toLowerCase().includes(searchTextLower) ||
    awayTeam.toLowerCase().includes(searchTextLower)
  );
};

const BetBody: React.FunctionComponent<BetBodyProps> = ({ isLoading = false }) => {
  const [searchParams] = useSearchParams();
  const search = useAppSelector(selectSearch);
  const dispatch = useAppDispatch();
  const games = useAppSelector(selectGames);
  const [filteredGamesWithSearch, setFilteredGamesWithSearch] =
    useState<GamesForTab>(initialGamesForTab);
  const [filteredGames, setFilteredGames] = useState<GameResponseModel[] | undefined>(undefined);
  const platform = useAppSelector(platformStates);

  const category = useMemo((): string | null => {
    return searchParams.get('category');
  }, [searchParams]);

  const tab = useMemo((): string | null => {
    return searchParams.get('tab');
  }, [searchParams]);

  useEffect(() => {
    if (!search.isSearchLayout) {
      if (category === 'tennis') {
        setFilteredGames(
          games
            ?.filter((game) => game.sport.title.includes(tab || 'ATP'))
            .filter((game) => !(game.status === 'LIVE' && isNotInLive(game.startDate)))
            .sort((a, b) =>
              compareCommenceTime(
                { id: a.id, startDate: a.startDate, isPinned: a.isPinned },
                { id: b.id, startDate: b.startDate, isPinned: b.isPinned },
              ),
            ),
        );
        return;
      } else {
        setFilteredGames(
          games
            ?.filter((game) => game.sport.title === tab)
            ?.filter((game) => !(game.status === 'LIVE' && isNotInLive(game.startDate)))
            .sort((a, b) =>
              compareCommenceTime(
                { id: a.id, startDate: a.startDate, isPinned: a.isPinned },
                { id: b.id, startDate: b.startDate, isPinned: b.isPinned },
              ),
            ),
        );
        return;
      }
    }
    const filteredGames: GamesForTab = Object.keys(initialGamesForTab).reduce((acc, key) => {
      acc[key] = [];
      return acc;
    }, {} as GamesForTab);

    games?.forEach((game) => {
      const sportTitle = game.sport.title;
      if (
        filteredGames[sportTitle] &&
        checkContain(game.homeTeam, game.awayTeam, search.searchText)
      ) {
        filteredGames[sportTitle].push(game);
      }
    });

    setFilteredGamesWithSearch(filteredGames);
  }, [games, search, tab]);

  useEffect(() => {
    if (search.isSearchLayout) {
      setFilteredGames(undefined);
    } else {
      setFilteredGamesWithSearch(initialGamesForTab);
    }
  }, [search.isSearchLayout]);

  useEffect(() => {
    dispatch(
      setSearch({
        searchText: '',
        isSearchLayout: false,
      }),
    );
  }, [tab, dispatch]);

  return (
    <>
      <div className="left-side">
        {!search.isSearchLayout &&
          (isPhone(platform) || isTablet(platform) || isLaptop(platform)) && <TabCategory />}
        {!search.isSearchLayout ? (
          <>
            <LeagueTab category={searchParams.get('category') || 'football'} tab={tab} />
            <MatchList
              filteredGames={filteredGames}
              category={searchParams.get('category') || 'football'}
              tab={tab}
            />
          </>
        ) : (
          <SearchLayout games={filteredGamesWithSearch} isLoading={!games} />
        )}
      </div>
    </>
  );
};

export default BetBody;
